import React, {Dispatch, SetStateAction} from "react"
import Button from "../../../components/Button/Button"
import RiskSettingsViewMode from "../../../components/RiskSettings/RiskSettingsViewMode"
import {
	RiskSettingsFieldNames,
	RiskSettingsType,
	showProhibitedBusinessVerticalsAndCountries,
} from "../../../components/RiskSettings/RiskSettingsUtils"
import {Badge} from "../../../components/Badge/Badge"
import {isEqualWith} from "lodash"
import {isEqualAsSet} from "../../../utilities/set"
import {getBankDetails} from "../../../utilities/getBankDetails"
import {BankNameAndLogo} from "../../../components/BankNameAndLogo/BankNameAndLogo"
import {GenericBankFallbackLogo} from "../../../images/bankLogos/GenericBankFallbackLogo"

type WithBankName = {bankName: string; showBankDetails: true}
type WithoutBankName = {bankName: undefined; showBankDetails: false}
export type BankDetails = WithBankName | WithoutBankName

interface OrgRiskSettingsViewModeProps {
	values: RiskSettingsType
	defaultValues: RiskSettingsType
	onResetToDefault: () => void
	setEditMode: Dispatch<SetStateAction<boolean>>
	canEdit: boolean
	bankId: string
	bankDetails: BankDetails
}

export default function OrgRiskSettingsViewMode({
	values,
	defaultValues,
	onResetToDefault,
	setEditMode,
	canEdit,
	bankId,
	bankDetails,
}: OrgRiskSettingsViewModeProps) {
	const setEditModeToTrue = () => setEditMode(true)
	const hasExceptions = showProhibitedBusinessVerticalsAndCountries
		? !isEqualWith(values, defaultValues, isEqualAsSet)
		: !isEqualWith(
				values[RiskSettingsFieldNames.AcceptableTypesOfIdentification],
				defaultValues[RiskSettingsFieldNames.AcceptableTypesOfIdentification],
				isEqualAsSet
		  )
	const {logo} = getBankDetails(bankId)
	const showOnboardingRulesSubtitle = bankDetails.showBankDetails || hasExceptions
	return (
		<div className="org-risk-settings-view-mode">
			<div className="mb-5 is-flex is-justify-content-space-between">
				<div className="risk-settings-view-mode-title">
					<h1 className="has-text-weight-bold is-size-4">Onboarding Rules</h1>
					{showOnboardingRulesSubtitle && (
						<div className="is-flex is-align-items-center">
							{bankDetails.showBankDetails && (
								<BankNameAndLogo
									name={bankDetails.bankName}
									logo={logo ?? GenericBankFallbackLogo}
									size={24}
									fill={"#FFFFFF"}
									className="bank-name-and-logo mr-3"
								/>
							)}
							{hasExceptions && (
								<Badge
									text="Includes Exceptions"
									hasChanges
									tooltip={`The onboarding rules for this<br>
										Org include exceptions to the<br>
										bank's default onboarding rules.`}
									tooltipMultiline
									tooltipPlace="bottom"
									tooltipClassname="risk-settings-badge-tooltip"
									tooltipKey="org-risk-settings-view-mode-tooltip"
									textClassName="risk-settings-view-mode-badge-span"
								/>
							)}
						</div>
					)}
				</div>
				{canEdit && (
					<div>
						<Button
							type="button"
							className="mr-3 pl-5"
							theme="danger"
							variant="outline"
							prefixIcon="edit-Arrow"
							onClick={onResetToDefault}
						>
							Reset to default
						</Button>
						<Button
							type="button"
							className="edit-button pr-6 pl-6"
							theme="neutral"
							variant="outline"
							prefixIcon="pencil-1--interface-essential"
							onClick={setEditModeToTrue}
						>
							<span>Edit</span>
						</Button>
					</div>
				)}
			</div>
			<RiskSettingsViewMode value={values} defaultValue={defaultValues} />
		</div>
	)
}
